import {DatePipe} from '@angular/common';
import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {finalize, switchMap} from 'rxjs/operators';
import {RouterLinks} from '../../constants';
import {GlobalQuestionService} from '../../services/global-question.service';
import {QuestionFlowSessionQuery} from '../state/question-flow-session.query';
import {QuestionQuery} from '../state/question.query';
import {QuestionStore} from '../state/question.store';
import {ShoppingCartStore} from '../state/shopping-cart-store';
import {Alert} from '../../utilities';
import {StorageService} from '../../services/storage.service';

declare var $: any;

@Component({
    selector: 'app-question-claim-summary',
    templateUrl: './question-claim-summary.component.html',
    styleUrls: ['./question-claim-summary.component.scss'],
    standalone: false
})
export class QuestionClaimSummaryComponent implements OnInit {

  @Output() showEditModal = new EventEmitter<any>();

  claim_name: string;
  location_line_1: string;
  location_line_2: string;
  summary: any[] = [];
  date: any;
  // Claim completion status IDs:
  // 1 = Incomplete (default, not yet evaluated)
  // 2 = Has Skipped Optional Questions (can submit)
  // 3 = Has Skipped Required Questions (cannot submit)
  // 4 = Complete
  completion_status_id: number | null = null;
  question_id: number;
  question: any;
  parameter: any;
  session: any;
  skipped_items: any[];
  event_details: any;
  police_report: any;
  claim: any;
  load_summary_update = false;
  submitting = false;

  constructor(
    private globalQuestionService: GlobalQuestionService,
    private datepipe: DatePipe,
    private router: Router,
    private questionSessionQuery: QuestionFlowSessionQuery,
    private shoppingCartStore: ShoppingCartStore,
    private questionStore: QuestionStore,
    private questionQuery: QuestionQuery,
    private storageService: StorageService) {
  }

  ngOnInit(): void {
    this.populateSummary();
  }

  populateSummary(){
    this.globalQuestionService.spinner(true);
    this.load_summary_update = false;
    this.session = this.questionSessionQuery.getValue();
    this.question_id = this.questionQuery.getActiveId();
    this.question = this.questionQuery.getEntity(this.question_id);
    this.parameter = this.question?.parameter?.split(',');
    this.globalQuestionService.getShoppingCart(0)
      .subscribe((summary: any[]) => {
        this.summary = summary;
        console.log(summary)
        this.globalQuestionService.evaluateClaimCompletionStatus()
          .pipe(switchMap(() => this.globalQuestionService.getClaim()))
          .subscribe((c: any[]) => {
            if (!c?.length) {
              return;
            }
            this.completion_status_id = c[0].claim_completion_status_id;
            this.claim = c[0];
            this.globalQuestionService.spinner(false);
          });

        const event_details = summary.find(entry => entry.entry_type === 'event_details');
        this.event_details = event_details;
        const police_report = summary.find(entry => entry.entry_type === 'police_report_details');
        this.police_report = police_report;

        const event_description = event_details?.description?.split('|');
        if (event_description?.length) {
          this.claim_name = event_description[0];

          const dateSplit = event_description[2]?.split('-');
          const year = dateSplit[0];
          const month = dateSplit[1];
          const day = dateSplit[2];

          const startDate = new Date(+year, +month - 1, +day);
          this.date = this.datepipe.transform(startDate, 'MMM dd, yyyy');

          if (event_description[1]) {
            try {
              const address = JSON.parse(event_description[1]);
              this.location_line_1 = `${address.streetAddress}, ${address.suburb}, `;
              this.location_line_2 = `${address.city}, ${address.postalCode}`;
            } catch {
              // event_description[1] wasn't valid address JSON - leave location blank rather than crash the summary
            }
          }
        }
        this.globalQuestionService.spinner(false);
      });
  }

  addAnother(): void {
    // this.globalQuestionService.spinner(true);
    // this.questionStore.setActive(2011);
    // this.router.navigate([`/${RouterLinks.QuestionFlow}`]);
    const question = this.questionQuery.getEntity(this.question_id);
    this.globalQuestionService.saveResponse(question, RouterLinks.QuestionFlow);
  }

  get isRegistered(): boolean {
    return this.claim?.claim_status_id === 2;
  }

  get hasClaimItems(): boolean {
    return !!this.summary?.some((item: any) => item.entry_type === 'class_item');
  }

  get primaryButtonLabel(): string {
    if (this.isRegistered) {
      return 'View Claim';
    }
    return this.completion_status_id === 3 ? 'Save' : 'Register Claim';
  }

  handlePrimaryAction(): void {
    if (this.submitting || !this.hasClaimItems) {
      return;
    }
    if (this.isRegistered) {
      this.saveClaim();
    } else if (this.completion_status_id === 3) {
      this.saveClaim();
    } else {
      this.submitClaim();
    }
  }

  saveClaim(): void {
    this.router.navigate(['/everything.claims-landing']);
  }

  submitClaim(): void {
    if (this.submitting) {
      return;
    }
   this.globalQuestionService.spinner(true);
    this.submitting = true;
    this.globalQuestionService.updateClaim(this.question_id, 2)
      .pipe(
        switchMap(() => this.globalQuestionService.setClaimRegistered()),
        switchMap(() => this.globalQuestionService.getClaim()),
        finalize(() => this.submitting = false)
      )
      .subscribe({
        next: (c: any[]) => {
          if (!c?.length) {
            return;
          }
          this.claim = c[0];
          this.globalQuestionService.spinner(false);
          $('#register-modal').modal('show');
        },
        error: () => {
          this.globalQuestionService.spinner(false);
          Alert('An error has occurred while registering your claim. Please try again.', 5000);
        }
      });
  }

  getClassHeaders(): any[] {
    return this.summary?.filter((item: any) => item.entry_type === 'class_header');
  }

  getClassItems(class_id: number): any {
    return this.summary?.filter((item: { class_id: number, entry_type: string; }) => item.class_id === class_id && item.entry_type === 'class_item');
  }

  getDescription(item: string, id: any): any {
    const splitItem = item?.split('|');
    if (splitItem) {
      switch (id) {
        case 1:
          return splitItem[0];
        case 2:
          return splitItem[1];
        case 3:
          return splitItem[2];
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  editItem(item: any): void {
    console.log(item?.shoppingcart_id)
    if (item) {
      this.shoppingCartStore.setActive(item?.shoppingcart_id);
      //this.shoppingCartStore.setActive(6);
      // this.globalQuestionService.spinner(true);
      // so navbar's closeClaimEdit() can find its way back here from claims-summary-update
      // (and from claim-edit, if that's reached from there too) instead of going nowhere/wrong.
      this.storageService.set('previous-document-page', this.router.url, 'flow');
      this.router.navigate([`/${RouterLinks.ClaimSummaryUpdate}`]);
      // this.globalQuestionService.spinner(false);
    }
  }

  removeItem(item: any): void {
    if(item?.class_id === 0 || item?.item_id === '0') return;
    this.globalQuestionService.spinner(true);
    const parameters = new Map<string, string>();
    parameters.set('session_id', this.session?.session_id);
    parameters.set('product_class_id', item?.class_id);
    parameters.set('item_id', item?.item_id);
    this.globalQuestionService.runCommandNew('removeItem', parameters)
      .subscribe(x => {
        this.populateSummary();
      });
  }

}
